/** SUNTRONIX ONE — Quote 360 action bar + lifecycle dialogs.
 *  EDIT / REVISE / DUPLICATE / DELETE / CANCEL / MARK LOST / PDF / HISTORY. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import QuoteWonDialog from "@/components/app/QuoteWonDialog";
import { QuoteFitCheck } from "@/components/commercial/QuoteFitCheck";
import { Pencil, FileText, Send, Copy, History, MoreHorizontal, Trash2, Ban, ThumbsDown, Trophy, GitBranch, Share2, Check, RefreshCw, Link2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DRAFT_STATES = ["DRAFT", "INTERNAL_REVIEW"];
const TERMINAL = ["CANCELLED", "LOST", "CONVERTED", "SUPERSEDED"];

export function QuoteActionBar({ q, onChanged, onEdit, onFitChanged }) {
  const [share, setShare] = useState(null); // { shared, evidence } — status SENT alone is not proof of sharing
  useEffect(() => { let ok = true; setShare(null); api.get(`/vtiger/quotations/${q.id}/share-state`).then((r) => ok && setShare(r.data)).catch(() => ok && setShare({ error: true })); return () => { ok = false; }; }, [q.id, q.status]);
  const locked = ["ACCEPTED", "CONVERTED"].includes(q.status);
  const isDraft = DRAFT_STATES.includes(q.status) || (share && !share.error && !share.shared && !TERMINAL.includes(q.status) && !locked);
  const [dialog, setDialog] = useState(null); // revise|cancel|lost|duplicate|history|delete|won
  const [busy, setBusy] = useState(false);
  const [delErr, setDelErr] = useState("");
  const [imgMode, setImgMode] = useState((q.image_mode || "COMPACT").toUpperCase());
  useEffect(() => { setImgMode((q.image_mode || "COMPACT").toUpperCase()); }, [q.image_mode]);

  const [cp, setCp] = useState(null);   // corporate profile flag state
  useEffect(() => {
    let ok = true;
    api.get(`/vtiger/quotations/${q.id}/corporate-profile`)
      .then((r) => { if (ok) setCp(r.data); }).catch(() => {});
    return () => { ok = false; };
  }, [q.id]);

  const toggleCorporateProfile = async (val) => {
    const prev = cp; setCp((p) => ({ ...p, include_corporate_profile: val, manual_override: true }));
    try {
      await api.patch(`/vtiger/quotations/${q.id}/corporate-profile`, { include: val });
      toast.success(val ? "Corporate Profile will be included in the PDF" : "Corporate Profile removed from the PDF");
    } catch (e) { setCp(prev); toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const setImageMode = async (m) => {
    const prev = imgMode; setImgMode(m);
    try {
      await api.patch(`/vtiger/quotations/${q.id}`, { image_mode: m });
      toast.success(`Quote product images: ${m.toLowerCase()}`);
      onChanged?.();
    } catch (e) { setImgMode(prev); toast.error(e.response?.data?.detail || "Could not update image mode"); }
  };

  const issue = async () => {
    if (!window.confirm(isDraft
      ? `Issue & send ${q.code} to ${q.customer_email || "the customer"}? This freezes an immutable PDF snapshot.`
      : `Resend ${q.code}? A new PDF version will be issued and emailed.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/vtiger/quotations/${q.id}/issue`);
      toast.success(`Issued v${data.version}${data.email?.status === "SENT" ? " — emailed to " + data.email.to : ""}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/vtiger/quotations/${q.id}`);
      toast.success(`${q.code} deleted — archived and recoverable`);
      setDialog(null); onChanged();
    } catch (e) { const det = e.response?.data?.detail; setDelErr(typeof det === "string" ? det : "Delete blocked"); }
    setBusy(false);
  };

  const revise = async (reason, note) => {
    setBusy(true);
    try {
      // No-draft policy: nothing is created up-front — the editor opens on the issued quote and
      // Save writes the change as the next revision (-R1, -R2 …), SENT; the original stays preserved.
      const { data: full } = await api.get(`/vtiger/quotations/${q.id}`);
      toast.success(`Editing ${q.code} — Save creates revision -R${(full.revision_no || 0) + 1} with your changes; ${q.code} stays preserved`);
      setDialog(null);
      onEdit({ ...full, _revision_reason: reason, _revision_note: note });
    } catch (e) { toast.error(e.response?.data?.detail || "Revise failed"); }
    setBusy(false);
  };

  const btn = "h-8 px-3 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5";

  return (
    <div className="mt-4 space-y-2" data-testid="quote-action-wrap">
      <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-md bg-slate-50/60 p-2"
        data-testid="quote-action-bar">
      {locked ? (
        <span className={`${btn} border border-emerald-200 bg-emerald-50 text-emerald-800`} data-testid="quote-action-locked">
          {q.status} — locked; edits and revisions are blocked
        </span>
      ) : !share && !DRAFT_STATES.includes(q.status) && !TERMINAL.includes(q.status) ? (
        <span className={`${btn} border border-slate-200 bg-white text-slate-500`} data-testid="quote-action-checking">Checking share status…</span>
      ) : isDraft ? (
        <button onClick={() => onEdit(q)} data-testid="quote-action-edit"
          className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>
          <Pencil className="h-3.5 w-3.5" /> Edit / Modify
        </button>
      ) : !TERMINAL.includes(q.status) && (
        <button onClick={() => setDialog("revise")} data-testid="quote-action-revise"
          className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>
          <GitBranch className="h-3.5 w-3.5" /> Edit → new revision
        </button>
      )}
      {share && !locked && !TERMINAL.includes(q.status) && (
        <span className="text-[11px] text-slate-600 max-w-[420px]" title={share.evidence?.join("; ") || ""} data-testid="quote-share-explain">
          {share.error ? "Couldn't confirm share status — saving decides (shared offers need a revision)."
            : share.shared ? `Shared (${share.evidence.join("; ")}) — changes are issued as the next revision; ${q.code} stays unchanged.`
            : `Not yet shared — edits keep ${q.code} (no revision).`}
        </span>
      )}
      <div className="inline-flex items-center rounded-md border border-slate-300 overflow-hidden" data-testid="quote-preview-density">
        <a href={`${API}/api/vtiger/quotations/${q.id}/pdf/preview?spec=detailed`} target="_blank" rel="noreferrer"
          data-testid="quote-action-preview-pdf"
          className="inline-flex items-center gap-1 px-2.5 h-[30px] text-[12px] text-slate-700 hover:bg-white">
          <FileText className="h-3.5 w-3.5" /> Preview PDF
        </a>
        <span className="w-px self-stretch bg-slate-300" />
        <a href={`${API}/api/vtiger/quotations/${q.id}/pdf/preview?spec=compact`} target="_blank" rel="noreferrer"
          data-testid="quote-action-preview-pdf-compact" title="Preview with compact specifications"
          className="inline-flex items-center gap-1 px-2.5 h-[30px] text-[12px] text-slate-500 hover:bg-white hover:text-slate-700">
          Compact
        </a>
        <span className="w-px self-stretch bg-slate-300" />
        <QuoteFitCheck q={q} onFitChanged={onFitChanged} />
      </div>

      {cp && (
        <div className="inline-flex items-center gap-2" data-testid="quote-corporate-profile">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-700 cursor-pointer select-none"
            title="Append the approved 2-page SUNTRONIX Corporate Profile to this quotation's PDF">
            <input type="checkbox" checked={!!cp.include_corporate_profile}
              onChange={(e) => toggleCorporateProfile(e.target.checked)}
              data-testid="corporate-profile-toggle" />
            Include SUNTRONIX Corporate Profile
          </label>
          {cp.is_first_customer_quote && (
            <span className="inline-flex flex-col leading-tight" data-testid="first-quote-indicator">
              <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">First Quote for Customer</Badge>
            </span>
          )}
        </div>
      )}
      {!TERMINAL.includes(q.status) && (
        <button onClick={issue} disabled={busy} data-testid="quote-action-send"
          className={`${btn} bg-emerald-700 text-white hover:bg-emerald-800`}>
          <Send className="h-3.5 w-3.5" /> {q.quote_version >= 1 ? "Resend" : "Issue & Send"}
        </button>
      )}
      <button onClick={() => setDialog("duplicate")} data-testid="quote-action-duplicate"
        className={`${btn} border border-slate-300 text-slate-700 hover:bg-white`}>
        <Copy className="h-3.5 w-3.5" /> Duplicate
      </button>
      <a href={`${API}/api/vtiger/quotations/${q.id}/pdf/download`}
        data-testid="quote-action-download-pdf"
        className={`${btn} border border-slate-300 text-slate-700 hover:bg-white`}>
        <FileText className="h-3.5 w-3.5" /> Download
      </a>
      <button onClick={() => setDialog("history")} data-testid="quote-action-history"
        className={`${btn} border border-slate-300 text-slate-700 hover:bg-white`}>
        <History className="h-3.5 w-3.5" /> History
      </button>
      {!TERMINAL.includes(q.status) && (
        <button onClick={() => setDialog("share")} data-testid="quote-action-share"
          className={`${btn} border border-slate-300 text-slate-700 hover:bg-white`}>
          <Share2 className="h-3.5 w-3.5" /> Share to accept
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${btn} border border-slate-300 text-slate-700 hover:bg-white`}
            data-testid="quote-action-more">
            <MoreHorizontal className="h-3.5 w-3.5" /> More
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[120]">
          {!["ACCEPTED", "CONVERTED"].includes(q.status) && (
            <DropdownMenuItem onClick={() => { setDelErr(""); setDialog("delete"); }} data-testid="quote-action-delete"
              className="text-rose-700 focus:text-rose-700">
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete (archive)
            </DropdownMenuItem>
          )}
          {!TERMINAL.includes(q.status) && (
            <DropdownMenuItem onClick={() => setDialog("cancel")} data-testid="quote-action-cancel">
              <Ban className="h-3.5 w-3.5 mr-2" /> Cancel quote
            </DropdownMenuItem>
          )}
          {!TERMINAL.includes(q.status) && q.status !== "ACCEPTED" && (
            <DropdownMenuItem onClick={() => setDialog("lost")} data-testid="quote-action-lost">
              <ThumbsDown className="h-3.5 w-3.5 mr-2" /> Mark as Lost
            </DropdownMenuItem>
          )}
          {!TERMINAL.includes(q.status) && q.status !== "ACCEPTED" && (
            <DropdownMenuItem onClick={() => setDialog("won")} data-testid="quote-action-won">
              <Trophy className="h-3.5 w-3.5 mr-2" /> Mark Won · PO Received
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ml-auto flex items-center gap-1.5" data-testid="quote-image-mode"
        title="Product image style on the quote PDF">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Images</span>
        {["COMPACT", "CATALOGUE", "OFF"].map((m) => (
          <button key={m} onClick={() => setImageMode(m)} data-testid={`quote-imgmode-${m.toLowerCase()}`}
            className={"h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors " +
              (imgMode === m ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {m === "COMPACT" ? "Compact" : m === "CATALOGUE" ? "Catalogue" : "Off"}
          </button>
        ))}
      </div>

      {dialog === "delete" && (
        <Dialog open onOpenChange={() => setDialog(null)}>
          <DialogContent className="sm:max-w-md z-[130]" data-testid="quote-delete-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Delete quotation?</DialogTitle>
              <DialogDescription className="text-[12px]">
                Quotation: <b>{q.code}</b><br />Customer: <b>{q.customer_name || "—"}</b><br /><br />
                The quotation is archived, not erased — an admin can restore it. Quotes that were shared with the customer, or that have a PO, order or later revision, can't be deleted.
                {share?.shared && <><br /><br /><span className="text-amber-800" data-testid="quote-delete-shared">Shared evidence: {share.evidence.join("; ")}</span></>}
                {delErr && <><br /><br /><span className="font-semibold text-rose-700" data-testid="quote-delete-error">{delErr}</span></>}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDialog(null)}>Cancel</Button>
              <Button size="sm" onClick={doDelete} disabled={busy}
                className="bg-rose-700 hover:bg-rose-800" data-testid="quote-delete-confirm">
                Delete quotation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {dialog === "revise" && (
        <ReasonDialog title={`Create revision of ${q.code}`}
          description="The original issued quote stays untouched. A new draft revision is created for editing."
          reasonsUrl="/vtiger/quotations/reasons/revision"
          submitLabel="Create revision" busy={busy}
          onSubmit={revise} onClose={() => setDialog(null)} testid="quote-revise-dialog" />
      )}
      {dialog === "cancel" && (
        <ReasonDialog title={`Cancel ${q.code}`}
          description="The quote and its history remain on record with status CANCELLED."
          freeReason submitLabel="Cancel quote" busy={busy}
          onSubmit={async (reason, note) => {
            setBusy(true);
            try {
              await api.post(`/vtiger/quotations/${q.id}/cancel`, { reason, note });
              toast.success(`${q.code} cancelled`); setDialog(null); onChanged();
            } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
            setBusy(false);
          }} onClose={() => setDialog(null)} testid="quote-cancel-dialog" />
      )}
      {dialog === "lost" && (
        <LostDialog q={q} busy={busy} onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); onChanged(); }} setBusy={setBusy} />
      )}
      {dialog === "duplicate" && (
        <DuplicateDialog q={q} onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); onChanged(); }} />
      )}
      {dialog === "history" && (
        <HistoryDialog q={q} onClose={() => setDialog(null)} />
      )}
      {dialog === "share" && (
        <ShareAcceptDialog q={q} onClose={() => setDialog(null)} />
      )}
      {dialog === "won" && (
        <QuoteWonDialog open onOpenChange={() => setDialog(null)}
          quoteId={q.id} quoteTitle={`${q.code} · ${q.subject || ""}`}
          onWon={() => { setDialog(null); onChanged(); }} />
      )}
      </div>
    </div>
  );
}

const ReasonDialog = ({ title, description, reasonsUrl, freeReason, submitLabel, busy, onSubmit, onClose, testid }) => {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (reasonsUrl) api.get(reasonsUrl).then(({ data }) => setReasons(data.items || [])).catch(() => {});
  }, [reasonsUrl]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md z-[130]" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-[12px]">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-1">Reason *</div>
            {freeReason || reasons.length === 0 ? (
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Reason" data-testid={`${testid}-reason`} />
            ) : (
              <select value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white"
                data-testid={`${testid}-reason`}>
                <option value="">Select a reason…</option>
                {reasons.map((r) => <option key={r}>{r}</option>)}
              </select>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-1">Note (optional)</div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${testid}-note`} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Back</Button>
          <Button size="sm" disabled={busy || !reason}
            onClick={() => onSubmit(reason, note || null)}
            data-testid={`${testid}-confirm`}>{submitLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LostDialog = ({ q, busy, setBusy, onClose, onDone }) => {
  const [f, setF] = useState({ reason: "", reason_category: "Price", competitor: "", comments: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md z-[130]" data-testid="quote-lost-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Mark {q.code} as Lost</DialogTitle>
          <DialogDescription className="text-[12px]">The quotation history is retained — nothing is deleted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-1">Reason *</div>
            <Input value={f.reason} onChange={set("reason")} data-testid="quote-lost-reason" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <div className="text-[10px] uppercase text-slate-400 mb-1">Category</div>
              <select value={f.reason_category} onChange={set("reason_category")}
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                {["Price", "Timeline", "Specification", "Competitor", "Budget Dropped", "No Response", "Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-400 mb-1">Competitor (if known)</div>
              <Input value={f.competitor} onChange={set("competitor")} data-testid="quote-lost-competitor" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-1">Comments</div>
            <Input value={f.comments} onChange={set("comments")} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Back</Button>
          <Button size="sm" disabled={busy || !f.reason} data-testid="quote-lost-confirm"
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/vtiger/quotations/${q.id}/mark-lost`, f);
                toast.success(`${q.code} marked LOST`); onDone();
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              setBusy(false);
            }}>Mark as Lost</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DuplicateDialog = ({ q, onClose, onDone }) => {
  const [mode, setMode] = useState("same");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (mode !== "other" || !query.trim()) { setHits([]); return; }
    const t = setTimeout(() => api.get("/vtiger/crm-customers", { params: { q: query, limit: 8 } })
      .then(({ data }) => setHits(data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [query, mode]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vtiger/quotations/${q.id}/duplicate`,
        { customer_account_id: mode === "other" ? target?.id : null });
      toast.success(`Duplicated to new draft ${data.code}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md z-[130]" data-testid="quote-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Duplicate {q.code}</DialogTitle>
          <DialogDescription className="text-[12px]">
            Creates a new SENT-stage quotation with the next STX-Q number. Products, descriptions, quantities and terms are copied exactly — POs, orders and send history are not.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <button onClick={() => setMode("same")} data-testid="quote-dup-same"
            className={`flex-1 text-[12px] rounded-md border px-3 py-2 ${mode === "same" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
            Same customer<br /><span className="text-[10px] opacity-70">{q.customer_name}</span>
          </button>
          <button onClick={() => setMode("other")} data-testid="quote-dup-other"
            className={`flex-1 text-[12px] rounded-md border px-3 py-2 ${mode === "other" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
            Different customer
          </button>
        </div>
        {mode === "other" && (
          <div>
            {target ? (
              <div className="flex items-center gap-2 text-[12px] border border-emerald-200 bg-emerald-50 rounded px-2 py-1.5">
                <span className="font-medium">{target.account_name}</span>
                <button className="ml-auto text-slate-500" onClick={() => setTarget(null)}>change</button>
              </div>
            ) : (
              <>
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search customer…" data-testid="quote-dup-search" />
                <div className="max-h-40 overflow-y-auto mt-1 border border-slate-100 rounded divide-y">
                  {hits.map((h) => (
                    <button key={h.id} onClick={() => setTarget(h)}
                      className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-slate-50">
                      {h.account_name} <span className="text-slate-400">· {h.customer_code}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Back</Button>
          <Button size="sm" onClick={run} disabled={busy || (mode === "other" && !target)}
            data-testid="quote-duplicate-confirm">Duplicate</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LABEL_TONE = {
  CURRENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SUPERSEDED: "bg-slate-100 text-slate-500 border-slate-200",
  "DRAFT REVISION": "bg-amber-50 text-amber-700 border-amber-200",
  DRAFT: "bg-amber-50 text-amber-700 border-amber-200",
};

const HistoryDialog = ({ q, onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/vtiger/quotations/${q.id}/revisions`)
      .then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [q.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg z-[130] max-h-[80vh] overflow-y-auto" data-testid="quote-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Version / Revision history</DialogTitle>
          <DialogDescription className="text-[12px]">{q.code} family — originals are never overwritten.</DialogDescription>
        </DialogHeader>
        {!items && <div className="text-[12px] text-slate-400">Loading…</div>}
        <div className="space-y-2">
          {(items || []).map((r) => (
            <div key={r.id} className={`border rounded-md p-3 ${r.is_this ? "border-slate-900" : "border-slate-200"}`}
              data-testid={`quote-rev-${r.code}`}>
              <div className="flex items-center gap-2 flex-wrap text-[12px]">
                <span className="font-mono font-semibold">{r.code}</span>
                <Badge className={"text-[10px] border " + (LABEL_TONE[r.label] || "bg-slate-100 text-slate-500 border-slate-200")}>{r.label}</Badge>
                <span className="text-slate-400">{r.status}</span>
                <span className="ml-auto text-slate-400">
                  {(r.issued_at || r.created_at || "").slice(0, 10)} · {(r.created_by || "").split("@")[0]}
                </span>
              </div>
              {r.revision_reason && (
                <div className="text-[11px] text-slate-500 mt-1">Reason: {r.revision_reason}</div>
              )}
              {r.changes?.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {r.changes.map((c, i) => (
                    <div key={i} className="text-[11px] text-slate-600">
                      <span className="font-medium">{c.field}:</span>{" "}
                      <span className="line-through text-slate-400">{String(c.before ?? "—")}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{String(c.after ?? "—")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ShareAcceptDialog = ({ q, onClose }) => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/digital-quote/${q.id}`); setInfo(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load the digital quote"); }
  }, [q.id]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load, q.id]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(info.url); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg z-[130]" data-testid="quote-share-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Share {q.code} — Digital Quotation</DialogTitle>
          <DialogDescription className="text-[12px]">
            One secure link per quotation revision (also printed as the QR on page 1 of the PDF). The customer reviews the exact snapshot, downloads the PDF and can accept or request a revision — every action is audited.
          </DialogDescription>
        </DialogHeader>
        {!info ? <div className="text-[12px] text-slate-400 py-2">Loading…</div> : !info.exists ? (
          <div className="text-[12px] text-slate-500 py-2">Digital quotes are created automatically for STX-Q quotations only.</div>
        ) : (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
              <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
              <input readOnly value={info.url} data-testid="quote-share-url" className="flex-1 bg-transparent text-[12px] text-slate-700 outline-none select-all" />
              <button onClick={copy} data-testid="quote-share-copy" className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-700 hover:text-slate-900">
                {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
            <div className="text-[11px] text-slate-400" data-testid="quote-share-meta">
              {info.status} · {info.views || 0} view{(info.views || 0) === 1 ? "" : "s"} · {info.pdf_downloads || 0} PDF download{(info.pdf_downloads || 0) === 1 ? "" : "s"}{info.expires_at ? ` · expires ${info.expires_at.slice(0, 10)}` : " · no expiry"} — manage access in the Digital Quote panel below.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteActionBar;